/**
 * services/sync/BackgroundSyncService.ts
 * Background sync service for automatic periodic syncing
 *
 * Singleton service that manages interval-based sync, handles page visibility
 * changes, and syncs on session close. Integrates with SyncEngine and respects
 * user preferences from SyncSettings.
 */

import { SyncEngine } from './SyncEngine';
import { SyncSettingsService } from '../SyncSettingsService';
import { imageStorageService } from '@/services/ImageStorageService';
import { assertBinarySupport } from '@/utils/syncProviderUtils';
import type { SyncSettings } from '@/types/sync/SyncSettings';
import type { SyncResult, SyncManifest } from '@/types/sync/SyncExport';
import type { SyncProvider, SchemaVersionError } from '@/types/sync/SyncProvider';
import { SYNC_MANIFEST_FILENAME } from '@/types/sync/SyncExport';

/**
 * Sync status for UI feedback
 */
export type SyncStatus = 'idle' | 'syncing' | 'success' | 'error';

/**
 * Background sync service (singleton)
 *
 * Manages automatic background sync with the following features:
 * - Periodic sync at configured intervals
 * - Pause/resume based on page visibility
 * - Sync on session close (beforeunload)
 * - Manual sync trigger
 * - Multi-tab coordination via SyncEngine lock
 */
export class BackgroundSyncService {
  private static instance: BackgroundSyncService | null = null;

  private syncEngine: SyncEngine | null = null;
  private syncSettingsService: SyncSettingsService;
  private settings: SyncSettings | null = null;

  private intervalId: number | null = null;
  private isRunning: boolean = false;
  private isPaused: boolean = false;

  private status: SyncStatus = 'idle';
  private lastSyncTime: string | null = null;
  private lastSyncError: string | null = null;
  private suppressBeforeUnload: boolean = false;
  private hasChangesSinceLastSync: boolean = false;
  private hiddenSyncTimeoutId: number | null = null; // Debounce timer for visibility-hidden sync

  // Pub/sub listeners for status updates (supports multiple subscribers)
  private statusListeners = new Set<(status: SyncStatus) => void>();
  private completedListeners = new Set<(result: SyncResult) => void>();

  // Schema version error state (for UI to display appropriate toast/status)
  private schemaVersionError: SchemaVersionError | null = null;
  private schemaVersionErrorListeners = new Set<(error: SchemaVersionError | null) => void>();

  /**
   * Private constructor (singleton pattern)
   */
  private constructor() {
    this.syncSettingsService = new SyncSettingsService();
  }

  /**
   * Get singleton instance
   *
   * @returns BackgroundSyncService instance
   */
  static getInstance(): BackgroundSyncService {
    if (!BackgroundSyncService.instance) {
      BackgroundSyncService.instance = new BackgroundSyncService();
    }
    return BackgroundSyncService.instance;
  }

  // ===== INITIALIZATION =====

  /**
   * Initialize the background sync service
   *
   * Must be called before starting sync. Sets up the sync engine and loads settings.
   *
   * @param userId - Current user ID
   * @param deviceId - Current device ID
   */
  async initialize(userId: string, deviceId: string): Promise<void> {
    console.log('🔄 Initializing BackgroundSyncService...');

    // Create sync engine (SyncEngine owns userId and deviceId)
    this.syncEngine = new SyncEngine(userId, deviceId);

    // Load sync settings
    this.settings = await this.syncSettingsService.getOrCreateDefault();

    console.log('✅ BackgroundSyncService initialized');
    console.log('  Sync enabled:', this.settings.syncEnabled);
    console.log('  Sync interval:', this.settings.syncInterval, 'minutes');
    console.log('  Active provider:', this.settings.activeSyncProvider || 'none');
  }

  // ===== LIFECYCLE =====

  /**
   * Start background sync service
   *
   * Begins periodic sync based on settings. Also sets up page visibility
   * and beforeunload listeners.
   *
   * @returns True if started successfully
   */
  async start(): Promise<boolean> {
    if (!this.syncEngine || !this.settings) {
      console.error('❌ Cannot start: BackgroundSyncService not initialized');
      return false;
    }

    if (!this.settings.syncEnabled) {
      console.log('⏸️ Sync disabled in settings, not starting');
      return false;
    }

    if (!this.settings.activeSyncProvider) {
      console.log('⏸️ No sync provider configured, not starting');
      return false;
    }

    // Verify provider is actually connected
    const provider = this.syncEngine.getProvider();
    if (!provider) {
      console.log('⏸️ Sync provider not set, not starting');
      return false;
    }

    const isConnected = await provider.isConnected();
    if (!isConnected) {
      console.log('⏸️ Sync provider not connected, not starting');
      return false;
    }

    if (this.isRunning) {
      console.log('⚠️ BackgroundSyncService already running');
      return false;
    }

    console.log('▶️ Starting BackgroundSyncService...');

    // Setup listeners
    this.setupVisibilityListener();
    this.setupHiddenSyncListener();
    this.setupBeforeUnloadListener();

    // Schedule first sync
    this.scheduleNextSync();

    // Hydrate lastSyncTime from manifest BEFORE marking as running
    // This ensures consumers reading getLastSyncTime() after isRunning=true
    // get the correct value (fallback for offline/before DB fetch)
    try {
      await this.loadLastSyncFromManifest();
    } catch (error) {
      console.warn('⚠️ Failed to load last sync from manifest:', error);
      // Non-fatal: service continues, lastSyncTime remains null
    }

    this.isRunning = true;
    console.log('✅ BackgroundSyncService started');
    return true;
  }

  /**
   * Stop background sync service
   *
   * Clears interval timer and removes event listeners.
   */
  stop(): void {
    if (!this.isRunning) {
      console.log('⚠️ BackgroundSyncService not running');
      return;
    }

    console.log('⏹️ Stopping BackgroundSyncService...');

    // Clear interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Remove listeners
    this.removeVisibilityListener();
    this.removeHiddenSyncListener();
    this.removeBeforeUnloadListener();

    this.isRunning = false;
    this.isPaused = false;

    // Clear any schema version errors and last sync error when stopping
    this.clearSchemaVersionError();
    this.lastSyncError = null;

    this.updateStatus('idle');

    console.log('✅ BackgroundSyncService stopped');
  }

  /**
   * Pause background sync
   *
   * Used when tab becomes hidden. Clears interval but keeps listeners active.
   */
  pause(): void {
    if (!this.isRunning || this.isPaused) {
      return;
    }

    console.log('⏸️ Pausing BackgroundSyncService (tab hidden)');

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.isPaused = true;
  }

  /**
   * Resume background sync
   *
   * Used when tab becomes visible. Restarts interval timer.
   */
  resume(): void {
    if (!this.isRunning || !this.isPaused) {
      return;
    }

    console.log('▶️ Resuming BackgroundSyncService (tab visible)');

    this.scheduleNextSync();
    this.isPaused = false;
  }

  // ===== SYNC EXECUTION =====

  /**
   * Load last sync info from manifest (if exists)
   *
   * Useful when reconnecting to an existing sync directory to show
   * the last sync timestamp without actually performing a sync.
   *
   * @returns Promise resolving to sync result from manifest, or null if no manifest
   */
  async loadLastSyncFromManifest(): Promise<SyncResult | null> {
    if (!this.syncEngine) {
      return null;
    }

    try {
      const provider = this.syncEngine.getProvider();
      if (!provider) {
        return null;
      }

      // Try to read the manifest
      const manifestJson = await provider.read(SYNC_MANIFEST_FILENAME);
      const manifest = JSON.parse(manifestJson);

      // Create a SyncResult from the manifest
      const result: SyncResult = {
        success: true,
        type: manifest.deltas && manifest.deltas.length > 0 ? 'delta' : 'baseline',
        filename: manifest.deltas && manifest.deltas.length > 0
          ? manifest.deltas[manifest.deltas.length - 1]
          : manifest.currentBaseline,
        dataExportedAt: manifest.lastExportedAt,
        recordCount: 0, // Manifest doesn't store this
        tableCount: 0    // Manifest doesn't store this
      };

      this.lastSyncTime = manifest.lastExportedAt;
      this.lastSyncError = null;

      return result;
    } catch (_error) {
      // No manifest or can't read it - that's ok, directory might be new
      console.log('No existing sync manifest found');
      return null;
    }
  }

  /**
   * Manually trigger a sync now
   *
   * Can be called from UI "Sync Now" button.
   *
   * @returns Promise resolving to sync result
   */
  async syncNow(): Promise<SyncResult> {
    if (!this.syncEngine) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync engine not initialized',
      };
      return errorResult;
    }

    // Check if service is running
    if (!this.isRunning) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync service is not running. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    // Check if provider is connected
    const provider = this.syncEngine.getProvider();
    if (!provider) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'No sync provider connected. Please connect to a sync location.',
      };
      return errorResult;
    }

    const isConnected = await provider.isConnected();
    if (!isConnected) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider is not connected. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    console.log('🔄 Manual sync triggered');
    return await this.performBackgroundSync(false);
  }

  /**
   * Trigger manual baseline sync (force baseline creation)
   *
   * Use this when overwriting directory data with device data.
   * Creates a new baseline regardless of delta count.
   *
   * @returns Promise resolving to sync result
   */
  async syncBaseline(): Promise<SyncResult> {
    // Validation (same as syncNow)
    if (!this.syncEngine) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync service is not running. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    const provider = this.syncEngine.getProvider();
    if (!provider) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'No sync provider connected. Please connect to a sync location.',
      };
      return errorResult;
    }

    const isConnected = await provider.isConnected();
    if (!isConnected) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider is not connected. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    console.log('📤 Forcing baseline creation (overwriting directory data)');
    return await this.performBackgroundSync(true);
  }

  /**
   * Force rebaseline from local data (recovery for corrupted cloud state)
   *
   * Use this when cloud data is corrupted (SCHEMA_VERSION_MISSING, INVALID, MISMATCH).
   * Skips import entirely and overwrites cloud with fresh baseline from local data.
   *
   * **Important**: This is a destructive operation that overwrites all cloud data.
   * UI should require explicit user confirmation before calling this method.
   *
   * @returns Promise resolving to sync result
   */
  async rebaselineFromLocal(): Promise<SyncResult> {
    // Validate service is running
    if (!this.syncEngine) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync service is not running. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    // Validate provider is connected
    const provider = this.syncEngine.getProvider();
    if (!provider) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'No sync provider connected. Please connect to a sync location.',
      };
      return errorResult;
    }

    const isConnected = await provider.isConnected();
    if (!isConnected) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider is not connected. Please reconnect to enable sync.',
      };
      return errorResult;
    }

    console.log('🔄 Force rebaseline from local (recovery mode)');

    try {
      this.updateStatus('syncing');

      // Call SyncEngine's forceRebaselineFromLocal
      const result = await this.syncEngine.forceRebaselineFromLocal();

      if (result.success) {
        // Clear all error state on success
        this.clearSchemaVersionError();
        this.lastSyncError = null;
        this.lastSyncTime = new Date().toISOString();
        this.hasChangesSinceLastSync = false;
        this.updateStatus('success');

        console.log('✅ Force rebaseline completed successfully');
      } else {
        // Set error state on failure
        this.lastSyncError = result.error ?? 'Rebaseline failed';
        this.updateStatus('error');

        console.error('❌ Force rebaseline failed:', result.error);
      }

      // Emit completed event so UI updates
      this.emitCompleted(result);

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during rebaseline';
      this.lastSyncError = errorMessage;
      this.updateStatus('error');

      console.error('❌ Force rebaseline threw exception:', errorMessage);

      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: errorMessage,
      };

      this.emitCompleted(errorResult);
      return errorResult;
    }
  }

  /**
   * Perform background sync
   *
   * Private method that executes the actual sync and updates status.
   *
   * @param forceBaseline - If true, always create baseline (skip delta logic)
   * @returns Promise resolving to sync result
   */
  private async performBackgroundSync(forceBaseline = false): Promise<SyncResult> {
    if (!this.syncEngine) {
      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync engine not initialized',
      };
      return errorResult;
    }

    try {
      this.updateStatus('syncing');

      // Use bidirectional sync: import (pull) then export (push)
      const result = await this.syncEngine.bidirectionalSync(forceBaseline);

      if (result.success) {
        this.lastSyncTime = new Date().toISOString();
        this.lastSyncError = null;
        this.hasChangesSinceLastSync = false; // Reset flag after successful sync
        this.updateStatus('success');
        console.log('✅ Background sync (bidirectional) completed successfully');

        // Clear schema version error on success (if any was previously set)
        this.clearSchemaVersionError();

        // Sync pending image blobs (after JSON sync success, before device updates)
        // Wrapped in try/catch - image sync failures must not affect JSON sync result
        try {
          await this.syncPendingImages();
        } catch (imageSyncError) {
          const message = imageSyncError instanceof Error ? imageSyncError.message : 'Unknown error';
          console.error(`📷 Image sync failed unexpectedly: ${message}`);
          // Continue - don't affect JSON sync result
        }

        // Update device's last_sync_at timestamp in database
        if (this.syncEngine) {
          try {
            const { DeviceService } = await import('../DeviceService');

            const deviceService = new DeviceService();
            const userId = this.syncEngine.getUserId();
            const deviceId = this.syncEngine.getDeviceId();

            // Update "Last Sync" (heartbeat) timestamp
            await deviceService.updateLastSync(userId, deviceId);
            console.log('📱 Device sync timestamp updated');

            // Update export/import timestamps based on what actually happened
            const timestamps: { exportedAt?: string; importedAt?: string } = {};

            // Export occurred when data was actually written (use SyncEngine's timestamp)
            if (result.dataExportedAt) {
              timestamps.exportedAt = result.dataExportedAt;
            }

            // Import occurred when remote data was processed (use SyncEngine's timestamp)
            if (result.importOccurred && result.dataImportedAt) {
              timestamps.importedAt = result.dataImportedAt;
            }

            // Only call if at least one timestamp to update
            if (timestamps.exportedAt || timestamps.importedAt) {
              // Fire-and-forget - don't await, don't block sync completion
              deviceService.updateSyncTimestamps(userId, deviceId, timestamps)
                .then(() => console.log('📱 Device export/import timestamps updated'))
                .catch((err) => console.error('⚠️ Failed to update export/import timestamps:', err));
            }
          } catch (deviceError) {
            console.error('⚠️ Failed to update device timestamp:', deviceError);
            // Don't fail the sync if device update fails
          }
        }
      } else {
        // Handle schema version errors specifically
        if (result.schemaVersionError) {
          // Store structured error for UI components
          const prevError = this.schemaVersionError;
          this.schemaVersionError = result.schemaVersionError;

          // Set human-readable error for non-schema-aware consumers
          this.lastSyncError = result.schemaVersionError.message;
          this.updateStatus('error');

          console.error('🛑 Schema version error:', {
            code: result.schemaVersionError.code,
            localVersion: result.schemaVersionError.localVersion,
            remoteVersion: result.schemaVersionError.remoteVersion,
            artifactType: result.schemaVersionError.artifactType,
            artifactName: result.schemaVersionError.artifactName,
          });

          // Only emit if value actually changed (prevents duplicate toasts)
          // Compare code, artifact, and versions - same artifact could have different version on retry
          if (
            prevError === null ||
            prevError.code !== result.schemaVersionError.code ||
            prevError.artifactName !== result.schemaVersionError.artifactName ||
            prevError.remoteVersion !== result.schemaVersionError.remoteVersion ||
            prevError.localVersion !== result.schemaVersionError.localVersion
          ) {
            this.emitSchemaVersionError(result.schemaVersionError);
          }
        } else {
          // Non-schema error
          this.lastSyncError = result.error || 'Unknown error';
          this.updateStatus('error');
          console.error('❌ Background sync failed:', this.lastSyncError);
        }
      }

      // Notify listeners
      this.emitCompleted(result);

      return result;
    } catch (error) {
      this.lastSyncError = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus('error');
      console.error('❌ Background sync exception:', error);

      const errorResult: SyncResult = {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: this.lastSyncError,
      };

      return errorResult;
    } finally {
      // Return to idle after a delay
      setTimeout(() => {
        if (this.status === 'success' || this.status === 'error') {
          this.updateStatus('idle');
        }
      }, 3000);
    }
  }

  /**
   * Sync pending image blobs to remote storage
   *
   * Uploads local image blobs that have not yet been synced (localSyncStatus
   * is 'pending', 'failed', or undefined). Processes sequentially to avoid
   * overwhelming the provider/network.
   *
   * This method is designed to never throw - all errors are caught and logged.
   * Individual upload failures do not stop processing of remaining images.
   *
   * @returns Promise that resolves when all pending images have been processed
   */
  private async syncPendingImages(): Promise<void> {
    // Get provider - if null, nothing to do
    const provider = this.syncEngine?.getProvider();
    if (!provider) {
      return;
    }

    // Check binary support - if not supported, skip silently (info log)
    try {
      assertBinarySupport(provider);
    } catch {
      console.log('📷 Image sync skipped - provider does not support binary operations');
      return;
    }

    // Check connectivity before starting uploads
    const isConnected = await provider.isConnected();
    if (!isConnected) {
      console.warn('📷 Image sync skipped - provider disconnected');
      return;
    }

    // Get pending blobs
    const pendingBlobs = await imageStorageService.getPendingBlobs();
    if (pendingBlobs.length === 0) {
      // Normal case - no log needed
      return;
    }

    // Initialize counters
    let uploaded = 0;
    let failed = 0;
    let skipped = 0;

    // Process each blob sequentially
    for (const blob of pendingBlobs) {
      // Get remote filename from metadata
      const remoteFilename = await imageStorageService.getRemoteFilename(blob.imageId);

      if (!remoteFilename) {
        // Orphan blob - metadata missing or deleted
        skipped++;
        console.warn(
          `📷 Skipping orphan blob ${blob.imageId} - no metadata found. ` +
            'Consider running imageStorageService.cleanupOrphanBlobs() to remove stale blobs.'
        );
        continue;
      }

      try {
        // Convert blob to ArrayBuffer
        const arrayBuffer = await blob.blob.arrayBuffer();

        // Determine MIME type with fallback
        const mimeType = blob.blob.type || 'application/octet-stream';

        // Upload to remote storage
        await provider.writeBinary(remoteFilename, arrayBuffer, mimeType);

        // Update status to synced
        await imageStorageService.updateBlobSyncStatus(blob.imageId, 'synced');
        uploaded++;
      } catch (error) {
        // Update status to failed (wrapped in try/catch to ensure loop continues)
        try {
          await imageStorageService.updateBlobSyncStatus(blob.imageId, 'failed');
        } catch (statusError) {
          const statusMessage = statusError instanceof Error ? statusError.message : 'Unknown error';
          console.warn(`📷 Failed to update status for image ${blob.imageId}: ${statusMessage}`);
        }
        failed++;

        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error(`📷 Failed to upload image ${blob.imageId}: ${message}`);

        // Check if provider disconnected - exit early to avoid hammering dead connection
        const stillConnected = await provider.isConnected();
        if (!stillConnected) {
          console.warn('📷 Provider disconnected mid-sync, stopping image uploads');
          break;
        }
      }
    }

    // Log summary
    if (failed === 0 && skipped === 0) {
      console.log(`✅ Image sync: ${uploaded} uploaded`);
    } else if (skipped > 0) {
      console.warn(
        `⚠️ Image sync: ${uploaded} uploaded, ${failed} failed, ${skipped} skipped (orphan blobs)`
      );
    } else {
      console.warn(`⚠️ Image sync: ${uploaded} uploaded, ${failed} failed`);
    }
  }

  /**
   * Schedule next sync based on interval setting
   */
  private scheduleNextSync(): void {
    if (!this.settings) {
      return;
    }

    // Clear existing interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }

    // Convert minutes to milliseconds
    const intervalMs = this.settings.syncInterval * 60 * 1000;

    // Set new interval
    this.intervalId = window.setInterval(() => {
      void this.performBackgroundSync();
    }, intervalMs);

    console.log(`⏰ Next sync scheduled in ${this.settings.syncInterval} minutes`);
  }

  // ===== SETTINGS MANAGEMENT =====

  /**
   * Update settings and restart sync if needed
   *
   * Call this when user changes sync settings in the UI.
   *
   * @param settings - Updated sync settings
   */
  async updateSettings(settings: SyncSettings): Promise<void> {
    this.settings = settings;

    if (this.isRunning) {
      // Restart with new settings
      this.stop();
      await this.start();
    }
  }

  // ===== EVENT LISTENERS =====

  /**
   * Setup page visibility listener
   *
   * Pauses sync when tab is hidden, resumes when visible.
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Remove page visibility listener
   */
  private removeVisibilityListener(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle visibility change event
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  };

  // ===== HIDDEN SYNC (visibility-based sync trigger) =====

  /**
   * Setup hidden sync listener
   *
   * Triggers sync when page becomes hidden for >5 seconds.
   * More reliable than beforeunload for mobile browsers.
   */
  private setupHiddenSyncListener(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChangeForSync);
  }

  /**
   * Remove hidden sync listener
   */
  private removeHiddenSyncListener(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChangeForSync);
    // Clear any pending timeout
    if (this.hiddenSyncTimeoutId !== null) {
      clearTimeout(this.hiddenSyncTimeoutId);
      this.hiddenSyncTimeoutId = null;
    }
  }

  /**
   * Handle visibility change for sync trigger
   *
   * When page becomes hidden, starts a 5-second debounce timer.
   * If user returns within 5s, timer is cancelled.
   * If timer fires, syncs if there are unsaved changes.
   */
  private handleVisibilityChangeForSync = (): void => {
    if (document.visibilityState === 'hidden') {
      // Page is now hidden - start debounce timer
      if (this.hiddenSyncTimeoutId !== null) {
        clearTimeout(this.hiddenSyncTimeoutId);
      }

      this.hiddenSyncTimeoutId = window.setTimeout(() => {
        this.hiddenSyncTimeoutId = null;

        // Guard: Only sync if there are changes and not already syncing
        if (!this.hasChangesSinceLastSync) {
          console.log('👁️ [HiddenSync] Page hidden >5s, but no changes to sync');
          return;
        }

        if (this.status === 'syncing') {
          console.log('👁️ [HiddenSync] Page hidden >5s, but sync already in progress');
          return;
        }

        if (!this.isRunning) {
          console.log('👁️ [HiddenSync] Page hidden >5s, but service not running');
          return;
        }

        console.log('👁️ [HiddenSync] Page hidden >5s with unsaved changes, triggering sync');
        void this.syncNow();
      }, 5000); // 5 second debounce
    } else {
      // Page is now visible - cancel debounce timer if pending
      if (this.hiddenSyncTimeoutId !== null) {
        clearTimeout(this.hiddenSyncTimeoutId);
        this.hiddenSyncTimeoutId = null;
        console.log('👁️ [HiddenSync] Page visible again, cancelled pending sync');
      }
    }
  };

  /**
   * Setup beforeunload listener
   *
   * Syncs data before window closes (if enabled in settings).
   */
  private setupBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Remove beforeunload listener
   */
  private removeBeforeUnloadListener(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Handle beforeunload event
   *
   * Shows warning dialog if there are unsynchronized changes.
   * NOTE: This is warning-only - async sync cannot reliably complete during
   * page unload. Use visibility-hidden sync for reliable pre-close sync.
   */
  private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    // Skip if suppressed (e.g., during directory picker dialogs)
    if (this.suppressBeforeUnload) {
      return;
    }

    if (!this.settings?.syncOnSessionClose) {
      return;
    }

    // Show warning if currently syncing OR if there are unsaved changes
    // This gives user a chance to cancel close and manually sync
    if (this.status === 'syncing') {
      console.log('⚠️ [BeforeUnload] Sync in progress - showing warning');
      event.preventDefault();
      event.returnValue = '';
    } else if (this.hasChangesSinceLastSync) {
      console.log('⚠️ [BeforeUnload] Unsaved changes exist - showing warning');
      event.preventDefault();
      event.returnValue = '';
    }
  };

  // ===== STATUS MANAGEMENT =====

  /**
   * Update sync status and notify listeners
   *
   * @param status - New sync status
   */
  private updateStatus(status: SyncStatus): void {
    this.status = status;
    this.emitStatus(status);
  }

  /**
   * Emit status to all subscribed listeners
   *
   * @param status - Status to emit
   */
  private emitStatus(status: SyncStatus): void {
    this.statusListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (err) {
        console.error('[BackgroundSyncService] Error in status listener:', err);
      }
    });
  }

  /**
   * Emit sync result to all subscribed listeners
   *
   * @param result - Sync result to emit
   */
  private emitCompleted(result: SyncResult): void {
    this.completedListeners.forEach((listener) => {
      try {
        listener(result);
      } catch (err) {
        console.error('[BackgroundSyncService] Error in completed listener:', err);
      }
    });
  }

  /**
   * Get current sync status
   *
   * @returns Current sync status
   */
  getStatus(): SyncStatus {
    return this.status;
  }

  /**
   * Get last sync time
   *
   * @returns ISO timestamp of last successful sync, or null
   */
  getLastSyncTime(): string | null {
    return this.lastSyncTime;
  }

  /**
   * Get last sync error
   *
   * @returns Last error message, or null
   */
  getLastSyncError(): string | null {
    return this.lastSyncError;
  }

  /**
   * Get current schema version error
   *
   * Returns the structured schema version error if import was blocked due to
   * version mismatch. UI components should check this for specific error
   * handling (toast messages, refresh prompts).
   *
   * @returns Schema version error details, or null if no schema error
   */
  getSchemaVersionError(): SchemaVersionError | null {
    return this.schemaVersionError;
  }

  /**
   * Clear schema version error state
   *
   * Called after user takes corrective action (refresh, reconnect).
   * Also called automatically on successful sync.
   */
  clearSchemaVersionError(): void {
    if (this.schemaVersionError !== null) {
      this.schemaVersionError = null;
      this.emitSchemaVersionError(null);
    }
  }

  /**
   * Subscribe to schema version error changes
   *
   * Callback fires when schema version error is set or cleared.
   * Only emits when value actually changes (prevents duplicate toasts).
   *
   * @param callback - Function to call when schema version error changes
   * @returns Unsubscribe function to remove the listener
   */
  onSchemaVersionError(callback: (error: SchemaVersionError | null) => void): () => void {
    this.schemaVersionErrorListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.schemaVersionErrorListeners.delete(callback);
    };
  }

  /**
   * Emit schema version error to all subscribed listeners
   *
   * @param error - Schema version error to emit (or null when cleared)
   */
  private emitSchemaVersionError(error: SchemaVersionError | null): void {
    this.schemaVersionErrorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (err) {
        console.error('[BackgroundSyncService] Error in schema version error listener:', err);
      }
    });
  }

  /**
   * Check if service is running
   *
   * @returns True if service is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Check if service is paused
   *
   * @returns True if service is paused
   */
  getIsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * Get current sync provider
   *
   * @returns Current sync provider, or null if not set
   */
  getProvider(): SyncProvider | null {
    return this.syncEngine?.getProvider() ?? null;
  }

  /**
   * Suppress beforeunload handler temporarily
   * Use this before opening file system dialogs
   */
  suppressBeforeUnloadHandler(): void {
    this.suppressBeforeUnload = true;
  }

  /**
   * Re-enable beforeunload handler
   * Call after file system dialogs close
   */
  enableBeforeUnloadHandler(): void {
    this.suppressBeforeUnload = false;
  }

  /**
   * Mark that data has changed since last sync
   *
   * Called by Dexie hooks when any data is created/updated/deleted.
   * Sets flag to true to trigger beforeunload warning.
   */
  markDataChanged(): void {
    // Only set if not already true (avoid redundant logging)
    if (!this.hasChangesSinceLastSync) {
      this.hasChangesSinceLastSync = true;
      console.log('📝 Unsaved changes detected');
    }
  }

  // ===== CALLBACKS =====

  /**
   * Subscribe to status changes
   *
   * Supports multiple subscribers - each call adds a new listener.
   *
   * @param callback - Function to call when status changes
   * @returns Unsubscribe function to remove the listener
   */
  onStatusChanged(callback: (status: SyncStatus) => void): () => void {
    this.statusListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.statusListeners.delete(callback);
    };
  }

  /**
   * Subscribe to sync completion events
   *
   * Supports multiple subscribers - each call adds a new listener.
   *
   * @param callback - Function to call when sync completes
   * @returns Unsubscribe function to remove the listener
   */
  onSyncCompleted(callback: (result: SyncResult) => void): () => void {
    this.completedListeners.add(callback);

    // Return unsubscribe function
    return () => {
      this.completedListeners.delete(callback);
    };
  }

  // ===== PROVIDER MANAGEMENT =====

  /**
   * Set the sync provider on the sync engine
   *
   * Must be called after provider is configured but before starting sync.
   *
   * @param provider - Configured sync provider instance
   */
  setProvider(provider: SyncProvider): void {
    if (!this.syncEngine) {
      console.error('❌ Cannot set provider: Sync engine not initialized');
      return;
    }

    this.syncEngine.setProvider(provider);
    console.log('✅ Sync provider set on BackgroundSyncService');
  }

  /**
   * Get the sync engine instance
   *
   * Useful for accessing sync engine methods directly (e.g., importFromProvider)
   * from UI components when needed.
   *
   * @returns SyncEngine instance or null if not initialized
   */
  getSyncEngine(): SyncEngine | null {
    return this.syncEngine;
  }

  /**
   * Load manifest from a provider (without initializing sync)
   *
   * Helper method for conflict detection when changing directories.
   * Reads the manifest file from a provider without modifying service state.
   *
   * @param provider - Sync provider to read manifest from
   * @returns Manifest or null if not found
   */
  async loadManifestFromProvider(provider: SyncProvider): Promise<SyncManifest | null> {
    try {
      const isConnected = await provider.isConnected();
      if (!isConnected) {
        return null;
      }

      const manifestJson = await provider.read('sync_manifest.json');
      const manifest: SyncManifest = JSON.parse(manifestJson);
      return manifest;
    } catch (_error) {
      // No manifest or can't read it - that's ok, directory might be new
      return null;
    }
  }
}

// ===== DEV ONLY - Testing Utilities =====

/**
 * Dev namespace for testing utilities
 * Gated behind import.meta.env.DEV for tree-shaking in production
 */
export const backgroundSyncServiceDev = import.meta.env.DEV ? {
  /**
   * Get service state
   */
  getState(): {
    isRunning: boolean;
    isPaused: boolean;
    status: string;
  } {
    const service = BackgroundSyncService.getInstance();
    return {
      isRunning: service.getIsRunning(),
      isPaused: service.getIsPaused(),
      status: service.getStatus(),
    };
  },

  /**
   * Set schema version error for UI testing
   *
   * Triggers the same emission as a real schema version error would,
   * allowing manual testing of header and settings page error states.
   *
   * @param error - Schema version error to set, or null to clear
   */
  setSchemaVersionError(error: SchemaVersionError | null): void {
    const service = BackgroundSyncService.getInstance();
    // DEV ONLY: Access private members for testing - `as any` is acceptable here because
    // this entire code block is tree-shakeable dev-only code (import.meta.env.DEV guard)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const serviceAny = service as any;

    console.log('[DEV] setSchemaVersionError:', error?.code ?? 'cleared');
    serviceAny.schemaVersionError = error;
    serviceAny.lastSyncError = error ? error.message : null;
    serviceAny.emitSchemaVersionError(error);
  },

  /**
   * Get current schema version error (for debugging)
   */
  getSchemaVersionError(): SchemaVersionError | null {
    const service = BackgroundSyncService.getInstance();
    return service.getSchemaVersionError();
  }
} : undefined;
